import React, { memo } from 'react'

import {
  bool, func, node, number, oneOf, string,
} from 'prop-types'

import { SEARCH_DEBOUNCE_MS } from 'Pods/Shared/GalleryFilters/constants'
import GalleryFilters from 'Pods/Shared/GalleryFilters/GalleryFilters'
import useDebouncedValue from 'Pods/Shared/GalleryFilters/Hooks/useDebouncedValue'
import useGalleryCategories from 'Pods/Shared/GalleryFilters/Hooks/useGalleryCategories'

import { NOOP } from 'Utils/stableDefaults'

import Gallery from './Components/Gallery/Gallery'
import { modelsQueryOptions } from './Components/Gallery/Hooks/useGalleryModels'
import { DEFAULT_COMMUNITY_GALLERY_FILTERS } from './constants'

// Controlled, host-agnostic gallery panel. The host owns filter state (a Zustand
// store on /ai-3d-model-generator/3d-models, local state elsewhere) and decides what a card click
// does, passing both down as values + `onX` handlers. The host also supplies the
// empty-state node (and, where it wants a "no exact match - showing popular"
// fallback, the notice node + `enableFallback`), so the panel itself stays free
// of any page-specific copy. The panel only fetches and renders: the category
// chips come from the `filterOptions` the gallery endpoint returns, not from the
// host. See the ThreeDStudio container for the canonical wiring.
function CommunityGalleryPanel({
  searchQuery = DEFAULT_COMMUNITY_GALLERY_FILTERS.searchQuery,
  onSearchChange = NOOP,
  category = DEFAULT_COMMUNITY_GALLERY_FILTERS.category,
  onCategoryChange = NOOP,
  sortOrder = DEFAULT_COMMUNITY_GALLERY_FILTERS.sortOrder,
  paginationMode = 'infinite',
  onCardClick = NOOP,
  onAuthGatedLoadMore = NOOP,
  anonymousPageLimit = null,
  emptyState = null,
  noMatchNotice = null,
  enableFallback = false,
  extractingCoreStyles = false,
  authLoaded = false,
  isUserLoggedIn = false,
  onSignInRequired = NOOP,
}) {
  // Debounced above both children so the chips and the grid subscribe to the same
  // query key; the input itself stays on the raw value.
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS)
  const categories = useGalleryCategories(modelsQueryOptions({
    searchQuery: debouncedSearchQuery, category, sortOrder,
  }))

  return (
    <div className="community-gallery-panel">
      <GalleryFilters
        className="community-gallery-panel__filters"
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        category={category}
        categories={categories}
        onCategoryChange={onCategoryChange}
      />
      <Gallery
        searchQuery={debouncedSearchQuery}
        category={category}
        sortOrder={sortOrder}
        paginationMode={paginationMode}
        onCardClick={onCardClick}
        onAuthGatedLoadMore={onAuthGatedLoadMore}
        anonymousPageLimit={anonymousPageLimit}
        emptyState={emptyState}
        noMatchNotice={noMatchNotice}
        enableFallback={enableFallback}
        extractingCoreStyles={extractingCoreStyles}
        authLoaded={authLoaded}
        isUserLoggedIn={isUserLoggedIn}
        onSignInRequired={onSignInRequired}
      />
    </div>
  )
}

CommunityGalleryPanel.propTypes = {
  searchQuery: string,
  onSearchChange: func,
  category: string,
  onCategoryChange: func,
  sortOrder: string,
  paginationMode: oneOf(['infinite', 'loadMore']),
  onCardClick: func,
  onAuthGatedLoadMore: func,
  anonymousPageLimit: number,
  emptyState: node,
  noMatchNotice: node,
  enableFallback: bool,
  extractingCoreStyles: bool,
  authLoaded: bool,
  isUserLoggedIn: bool,
  onSignInRequired: func,
}

export default memo(CommunityGalleryPanel)
